import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import IcShieldReportFill from 'src/assets/images/icShieldReportFill.svg';
import IcTrashFill from 'src/assets/images/icTrashFill.svg';

/** 아이콘이 완전히 드러나는 이동 거리 */
const SNAP_X = 88;
/** 아이콘이 100% 노출되는 지점(SNAP_X)까지 당겼을 때 액션 트리거 */
const THRESHOLD = SNAP_X;
interface Props {
  children: React.ReactNode;
  /** 우→좌 스와이프: 목록에서 제거 */
  onSwipeLeft: () => void;
  /** 좌→우 스와이프: 신고 */
  onSwipeRight: () => void;
}
const Root = styled.div`
  position: relative;
  overflow: hidden;
`;
const AnimatedCard = styled(animated.div)`
  width: 100%;
  touch-action: pan-y;
`;

/**
 * 신고 아이콘 오버레이: 좌→우 스와이프 시 왼쪽에서 나타남
 * inset-inline-end: 100% → LTR에서 컨테이너 왼쪽 바깥에서 시작
 * 카드와 동일한 x로 이동 → 카드가 오른쪽으로 밀리면 함께 들어옴
 */
const ReportOverlay = styled(animated.div)`
  pointer-events: none;
  position: absolute;
  inset-block: 0;
  inset-inline-end: 100%;
  width: ${SNAP_X}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * 제거 아이콘 오버레이: 우→좌 스와이프 시 오른쪽에서 나타남
 * inset-inline-start: 100% → LTR에서 컨테이너 오른쪽 바깥에서 시작
 * 카드와 동일한 x로 이동 → 카드가 왼쪽으로 밀리면 함께 들어옴
 */
const DeleteOverlay = styled(animated.div)`
  pointer-events: none;
  position: absolute;
  inset-block: 0;
  inset-inline-start: 100%;
  width: ${SNAP_X}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ActionIcon = styled.div<{
  $variant: 'report' | 'delete';
}>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({
  $variant,
  theme
}) => $variant === 'report' ? theme.color.accentA1 : theme.color.accentB1};

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
`;
export default function InviteUserCardSwipeWrapper({
  children,
  onSwipeLeft,
  onSwipeRight
}: Props) {
  const {
    isRtl
  } = useTheme();
  const [{
    x
  }, api] = useSpring(() => ({
    x: 0,
    config: {
      tension: 300,
      friction: 22
    }
  }));
  const bind = useDrag(({
    active,
    movement: [mx]
  }) => {
    // effectiveX > 0 이면 end 방향(LTR: 오른쪽 = 신고)
    const effectiveX = isRtl ? -mx : mx;
    if (active) {
      const clampedX = Math.max(-SNAP_X * 1.2, Math.min(SNAP_X * 1.2, mx));
      api.start({
        x: clampedX,
        immediate: true
      });
    } else {
      if (Math.abs(effectiveX) >= THRESHOLD) {
        const physicalSign = isRtl ? -Math.sign(effectiveX) : Math.sign(effectiveX);
        const isReport = effectiveX > 0;
        if (isReport) {
          // 신고: snap 후 400ms 대기 → 원위치 복귀
          api.start({
            x: physicalSign * SNAP_X,
            onRest: ({
              finished
            }) => {
              if (!finished) return;
              setTimeout(() => api.start({
                x: 0,
                config: {
                  tension: 280,
                  friction: 28
                }
              }), 400);
            }
          });
          onSwipeRight();
        } else {
          // 제거: snap 완료 후 onSwipeLeft 호출 — 즉시 호출하면 컴포넌트가 언마운트되어
          // spring 애니메이션이 한 프레임도 렌더되지 않고 사라짐
          api.start({
            x: physicalSign * SNAP_X,
            onRest: ({
              finished
            }) => {
              if (finished) onSwipeLeft();
            }
          });
        }
      } else {
        api.start({
          x: 0
        });
      }
    }
  }, {
    axis: 'x',
    filterTaps: true,
    preventDefault: true,
    eventOptions: {
      passive: false
    }
  });
  return <Root data-sentry-element="Root" data-sentry-component="InviteUserCardSwipeWrapper" data-sentry-source-file="InviteUserCardSwipeWrapper.tsx">
      <ReportOverlay style={{
      x
    }} data-sentry-element="ReportOverlay" data-sentry-source-file="InviteUserCardSwipeWrapper.tsx">
        <ActionIcon $variant='report' data-sentry-element="ActionIcon" data-sentry-source-file="InviteUserCardSwipeWrapper.tsx">
          <IcShieldReportFill data-sentry-element="IcShieldReportFill" data-sentry-source-file="InviteUserCardSwipeWrapper.tsx" />
        </ActionIcon>
      </ReportOverlay>
      <DeleteOverlay style={{
      x
    }} data-sentry-element="DeleteOverlay" data-sentry-source-file="InviteUserCardSwipeWrapper.tsx">
        <ActionIcon $variant='delete' data-sentry-element="ActionIcon" data-sentry-source-file="InviteUserCardSwipeWrapper.tsx">
          <IcTrashFill data-sentry-element="IcTrashFill" data-sentry-source-file="InviteUserCardSwipeWrapper.tsx" />
        </ActionIcon>
      </DeleteOverlay>
      <AnimatedCard {...bind()} style={{
      x
    }} data-sentry-element="AnimatedCard" data-sentry-source-file="InviteUserCardSwipeWrapper.tsx">
        {children}
      </AnimatedCard>
    </Root>;
}